import { useCallback, useEffect, useRef } from 'react';

/**
 * useLongPress — fires `onLongPress(event)` after `delay` ms of held pointer
 * input on the target element. Cancels on:
 *   - pointer release before the timer fires (treat as a short tap)
 *   - movement of more than `moveThreshold` px from the initial pointerdown
 *   - pointer leaving the element
 *
 * The returned object spreads onto the target as event-handler props:
 *
 *   const longPress = useLongPress(handler, { delay: 500 });
 *   <div {...longPress}>…</div>
 *
 * Plays nicely with onClick — a fast tap doesn't fire the long-press. If the
 * long-press DID fire, the consumer can read `longPress.didLongPressRef`
 * (a ref carrying a boolean) in their onClick to short-circuit normal click
 * behavior. Defaults: delay=500 (iOS native), moveThreshold=10px.
 *
 * Pointer events are preferred over touch events because they unify mouse,
 * touch, and pen — jsdom doesn't dispatch them but our tests fire them
 * directly via fireEvent.pointer* so this is fine.
 */
export function useLongPress(onLongPress, options = {}) {
  const { delay = 500, moveThreshold = 10 } = options;

  const timerRef = useRef(null);
  const startPosRef = useRef(null);
  const didLongPressRef = useRef(false);
  const callbackRef = useRef(onLongPress);

  // Keep the latest callback without re-creating handlers every render.
  useEffect(() => {
    callbackRef.current = onLongPress;
  }, [onLongPress]);

  const clear = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    startPosRef.current = null;
  }, []);

  // Belt-and-braces cleanup on unmount so a dangling timer can't fire
  // setState on a torn-down component.
  useEffect(() => () => clear(), [clear]);

  const onPointerDown = useCallback(
    (e) => {
      didLongPressRef.current = false;
      startPosRef.current = {
        x: e.clientX ?? 0,
        y: e.clientY ?? 0,
      };
      // Stash event-derived data eagerly — by the time setTimeout fires, the
      // synthetic event is pooled-and-nulled.
      const captured = { target: e.target, currentTarget: e.currentTarget };
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        didLongPressRef.current = true;
        timerRef.current = null;
        if (typeof callbackRef.current === 'function') {
          callbackRef.current(captured);
        }
      }, delay);
    },
    [delay],
  );

  const onPointerMove = useCallback(
    (e) => {
      const start = startPosRef.current;
      if (!start || !timerRef.current) return;
      const dx = (e.clientX ?? 0) - start.x;
      const dy = (e.clientY ?? 0) - start.y;
      if (Math.hypot(dx, dy) > moveThreshold) {
        clear();
      }
    },
    [moveThreshold, clear],
  );

  const onPointerUp = useCallback(() => {
    clear();
  }, [clear]);

  const onPointerLeave = useCallback(() => {
    clear();
  }, [clear]);

  const onPointerCancel = useCallback(() => {
    clear();
  }, [clear]);

  // Touch fallbacks — useful in jsdom-style tests and on browsers that don't
  // synthesize pointer events from touch. We mirror to the same handlers.
  const onTouchStart = useCallback(
    (e) => {
      const t = e.touches?.[0];
      onPointerDown({
        clientX: t?.clientX ?? 0,
        clientY: t?.clientY ?? 0,
        target: e.target,
        currentTarget: e.currentTarget,
      });
    },
    [onPointerDown],
  );

  const onTouchMove = useCallback(
    (e) => {
      const t = e.touches?.[0];
      onPointerMove({ clientX: t?.clientX ?? 0, clientY: t?.clientY ?? 0 });
    },
    [onPointerMove],
  );

  const onTouchEnd = useCallback(() => {
    clear();
  }, [clear]);

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerLeave,
    onPointerCancel,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    didLongPressRef,
  };
}

export default useLongPress;
